
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import dayjs from "dayjs";
import { getJielongRecords, JielongRecordItem } from "@/api/jielong";
import { downloadUrl, timeDiff } from "@/utils/util";

const FIRST_PAGE_SIZE = 3;
const MORE_PAGE_SIZE = 10;
const DEFAULT_AVATAR = require("@/assets/images/photo-bg.png");

@Component
export default class PreviewRecordsSection extends Vue {
  /** 有 joinActivityId 时才请求接龙记录接口；预览态传空则展示空态 */
  @Prop({ type: String, default: "" }) joinActivityId!: string;
  @Prop({ type: String, default: "" }) storeId!: string;

  records: JielongRecordItem[] = [];
  loading = false;
  loadingMore = false;
  hasMore = false;
  private pageIndex = 1;
  /** 是否已切到大分页（pageSize=10）；首次「查看更多」需仍用 pageIndex=1 */
  private expanded = false;
  private requestSeq = 0;

  get showMoreBtn (): boolean {
    return this.hasMore;
  }

  @Watch("joinActivityId", { immediate: true })
  onJoinActivityIdChange (): void {
    this.reload();
  }

  @Watch("storeId")
  onStoreIdChange (): void {
    this.reload();
  }

  /** 供父级刷新详情时同步刷新记录 */
  reload (): void {
    this.pageIndex = 1;
    this.expanded = false;
    this.hasMore = false;
    this.records = [];
    this.loadRecords(false);
  }

  private loadRecords (append: boolean): void {
    if (!this.joinActivityId || !this.storeId) {
      this.records = [];
      this.hasMore = false;
      this.loading = false;
      this.loadingMore = false;
      return;
    }

    // 首次：pageSize=3, pageIndex=1
    // 第一次「查看更多」：pageSize 改为 10，pageIndex 仍为 1（整表替换，避免漏数据/重复）
    // 之后「查看更多」：pageSize=10, pageIndex 递增追加
    const isExpandFirst = append && !this.expanded;
    const pageSize = append || this.expanded ? MORE_PAGE_SIZE : FIRST_PAGE_SIZE;
    const pageIndex = isExpandFirst ? 1 : (append ? this.pageIndex : 1);
    const seq = ++this.requestSeq;

    if (append) {
      this.loadingMore = true;
    } else {
      this.loading = true;
    }

    getJielongRecords({
      storeId: this.storeId,
      joinActivityId: this.joinActivityId,
      pageIndex,
      pageSize,
    })
      .then((res) => {
        if (seq !== this.requestSeq) return;
        const page = res.data;
        const list = [...(page.records || [])].sort(
          (a, b) => Number(b.sort || 0) - Number(a.sort || 0)
        );

        if (isExpandFirst) {
          // pageSize 变大后重新拉第 1 页，直接覆盖前 3 条
          this.records = list;
          this.expanded = true;
          this.pageIndex = 2;
        } else if (append) {
          this.records = this.records.concat(list);
          this.pageIndex = pageIndex + 1;
        } else {
          this.records = list;
          // 小分页首次加载后不 +1，下次查看更多仍从 pageIndex=1 拉大分页
          this.pageIndex = 1;
          this.expanded = false;
        }

        const currentPage = Number(page.pageIndex || pageIndex);
        const totalPages = Number(page.totalPages || 0);
        this.hasMore = totalPages > 0
          ? currentPage < totalPages
          : list.length >= pageSize;
      })
      .catch(() => {
        if (seq !== this.requestSeq) return;
        if (!append) {
          this.records = [];
        }
        this.hasMore = false;
      })
      .finally(() => {
        if (seq !== this.requestSeq) return;
        this.loading = false;
        this.loadingMore = false;
      });
  }

  getAvatar (record: JielongRecordItem): string {
    if (!record.headImg) return DEFAULT_AVATAR;
    if (/^https?:\/\//i.test(record.headImg)) return record.headImg;
    const sftpUrl = this.$store.state.user.sftpUrl || "";
    if (sftpUrl) return sftpUrl + record.headImg;
    return downloadUrl(record.headImg);
  }

  formatJoinTime (time: string): string {
    if (!time) return "";
    // 后端可能已返回相对时间（如「1分钟前」）
    if (/前$|刚刚/.test(time)) return time;
    const diffMinutes = timeDiff(time, "minute");
    if (Number.isNaN(diffMinutes)) return time;
    if (diffMinutes < 1) return "刚刚";
    if (diffMinutes < 60) return `${diffMinutes}分钟前`;
    const diffHours = timeDiff(time, "hour");
    if (diffHours < 24) return `${diffHours}小时前`;
    const diffDays = timeDiff(time, "day");
    if (diffDays < 30) return `${diffDays}天前`;
    return dayjs(time).format("MM-DD HH:mm");
  }

  onViewMore (): void {
    if (this.loadingMore || !this.hasMore) return;
    this.loadRecords(true);
  }
}
